import React, { useState, useEffect, useCallback } from 'react';
import './App.css';

import AppHeader from './components/AppHeader';
import Login from './components/Login';
import SeriesDashboard from './components/SeriesDashboard';
import SeriesOverview from './components/SeriesOverview';
import FileUpload from './components/FileUpload';
import FirstRaceReview from './components/FirstRaceReview';
import PointsConfiguration from './components/PointsConfiguration';
import PointsApplication from './components/PointsApplication';
import ParticipantMatching from './components/ParticipantMatching';
import ResultsPreview from './components/ResultsPreview';

import DataPublishing from './components/DataPublishing';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import DataDeletion from './components/DataDeletion';
import { ProcessingStage, AppData } from './types';
import AuthService from './services/authService';
import DataService from './services/dataService';

const App: React.FC = () => {
  // 🛡️ PRODUCTION SAFEGUARD: Check for local mode in production builds
  useEffect(() => {
    const localModeEnabled = process.env.REACT_APP_LOCAL_MODE === 'true';
    const isProduction = process.env.NODE_ENV === 'production';
    const isNotLocalhost = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
    
    if (localModeEnabled && (isProduction || isNotLocalhost)) {
      console.error('🚨 SECURITY ALERT: REACT_APP_LOCAL_MODE is enabled in production!');
      console.error('   This is a critical security vulnerability.');
      console.error('   Local mode must NEVER be deployed to production.');
      console.error('   Please contact development team immediately.');
      
      // Show user-visible error in production
      if (isProduction) {
        alert('SECURITY ERROR: Local development mode detected in production build. Please contact support.');
      }
    }
  }, []);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentStage, setCurrentStage] = useState<ProcessingStage>('workflow-selection');
  const [appData, setAppData] = useState<AppData>({
    workflowType: null,
    existingSeriesData: null,
    newRaceData: null,
    pointsScheme: '',
    processedRaceData: null,
    matchedParticipants: null,
    finalResults: null,
    currentSeriesMetadata: undefined
  });

  const authService = AuthService.getInstance();
  const dataService = DataService.getInstance();

  // Check if we're on a legal page route
  const path = window.location.pathname;
  const isLegalPage = path.includes('/privacy-policy') || path.includes('/terms-of-service') || path.includes('/data-deletion');

  useEffect(() => {
    // Handle OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    
    if (code && window.location.pathname === '/auth/callback') {
      // Handle OAuth callback
      authService.handleOAuthCallback(code).then(() => {
        // Clear URL params and redirect to home
        window.history.replaceState({}, '', '/');
      }).catch((error) => {
        console.error('OAuth callback error:', error);
        // Clear URL params and redirect to home
        window.history.replaceState({}, '', '/');
      });
    } else if (window.location.pathname === '/auth/logout') {
      window.history.replaceState({}, '', '/');
    } else if (error) {
      console.error('OAuth error:', error);
      // Clear URL params and redirect to home
      window.history.replaceState({}, '', '/');
    }
    
    // Check initial authentication state
    setIsAuthenticated(authService.isLoggedIn());
    
    // Subscribe to auth changes
    const unsubscribe = authService.subscribe((authState) => {
      setIsAuthenticated(authState.isAuthenticated);
      if (!authState.isAuthenticated) {
        // Reset to dashboard when logged out
        setCurrentStage('workflow-selection');
        setAppData({
          workflowType: null,
          existingSeriesData: null,
          newRaceData: null,
          pointsScheme: '',
          processedRaceData: null,
          matchedParticipants: null,
          finalResults: null,
          currentSeriesMetadata: undefined
        });
      }
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle successful login
  const handleLogin = useCallback(() => {
    setIsAuthenticated(true);
    setCurrentStage('workflow-selection'); // Start at dashboard
  }, []);

  // Handle series selection from dashboard
  const handleSelectSeries = useCallback(async (seriesId: string | null, action: 'existing' | 'new') => {
    try {
      if (action === 'new') {
        // Start new series workflow
        setAppData({
          workflowType: 'first-race',
          existingSeriesData: null,
          newRaceData: null,
          pointsScheme: '',
          processedRaceData: null,
          matchedParticipants: null,
          finalResults: null,
          currentSeriesMetadata: undefined
        });
        setCurrentStage('upload');
      } else if (seriesId) {
        // Load existing series
        const seriesData = await dataService.loadSeries(seriesId);
        setAppData({
          workflowType: 'subsequent-race',
          existingSeriesData: seriesData.participants,
          newRaceData: null,
          pointsScheme: seriesData.pointsScheme,
          processedRaceData: null,
          matchedParticipants: null,
          finalResults: null,
          currentSeriesMetadata: seriesData.metadata
        });
        setCurrentStage('series-overview');
      }
    } catch (error) {
      console.error('Failed to load series:', error);
      // Handle error - maybe show error message
    }
  }, [dataService]);

  // Return to dashboard
  const returnToHome = useCallback(() => {
    setCurrentStage('workflow-selection');
    setAppData({
      workflowType: null,
      existingSeriesData: null,
      newRaceData: null,
      pointsScheme: '',
      processedRaceData: null,
      matchedParticipants: null,
      finalResults: null,
      currentSeriesMetadata: undefined
    });
  }, []);

  // If it's a legal page, render it directly without authentication
  if (isLegalPage) {
    if (path.includes('/privacy-policy')) {
      return <PrivacyPolicy />;
    } else if (path.includes('/terms-of-service')) {
      return <TermsOfService />;
    } else if (path.includes('/data-deletion')) {
      return <DataDeletion />;
    }
  }

  // Define stages based on workflow type
  const getStagesForWorkflow = () => {
    if (currentStage === 'workflow-selection') {
      return [
        { id: 'workflow-selection', title: 'Dashboard', icon: '🏠' }
      ];
    } else if (appData.workflowType === 'first-race') {
      return [
        { id: 'workflow-selection', title: 'Dashboard', icon: '🏠' },
        { id: 'upload', title: 'Upload File', icon: '📁' },
        { id: 'first-race-review', title: 'Review Data', icon: '📋' },
        { id: 'points', title: 'Configure Points', icon: '🏆' },
        { id: 'apply', title: 'Apply Points', icon: '🔢' },
        { id: 'results', title: 'View Results', icon: '📊' },
        { id: 'publish', title: 'Publish', icon: '🌐' }
      ];
    } else if (appData.workflowType === 'subsequent-race') {
      return [
        { id: 'workflow-selection', title: 'Dashboard', icon: '🏠' },
        { id: 'series-overview', title: 'Series Overview', icon: '📊' },
        { id: 'upload', title: 'Upload Files', icon: '📁' },
        { id: 'points', title: 'Configure Points', icon: '🏆' },
        { id: 'apply', title: 'Apply Points', icon: '🔢' },
        { id: 'match', title: 'Match Participants', icon: '👥' },
        { id: 'results', title: 'View Results', icon: '📊' },
        { id: 'publish', title: 'Publish', icon: '🌐' }
      ];
    } else {
      return [
        { id: 'workflow-selection', title: 'Dashboard', icon: '🏠' }
      ];
    }
  };

  const stages = getStagesForWorkflow();
  const currentStageIndex = stages.findIndex(stage => stage.id === currentStage);

  const nextStage = () => {
    const nextIndex = currentStageIndex + 1;
    if (nextIndex < stages.length) {
      setCurrentStage(stages[nextIndex].id as ProcessingStage);
    }
  };

  const prevStage = () => {
    const prevIndex = currentStageIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStage(stages[prevIndex].id as ProcessingStage);
    }
  };

  const restartForAnotherRace = () => {
    // When processing another race, we already have series data so go to series overview
    setAppData({
      workflowType: 'subsequent-race',
      existingSeriesData: appData.finalResults || null, // Use the current final results as existing series data
      newRaceData: null,
      pointsScheme: appData.pointsScheme,
      processedRaceData: null,
      matchedParticipants: null,
      finalResults: null,
      currentSeriesMetadata: appData.currentSeriesMetadata // Preserve existing series metadata
    });
    setCurrentStage('series-overview'); // Go to series overview to manage the series
  };

  // Special navigation function for SeriesOverview
  const navigateToResults = () => {
    setCurrentStage('results');
  };

  const renderCurrentStage = () => {
    // Set up global navigation functions for SeriesOverview
    (window as any).__navigateToResults = navigateToResults;

    switch (currentStage) {
      case 'workflow-selection':
        return (
          <SeriesDashboard 
            onSelectSeries={handleSelectSeries}
          />
        );
      case 'series-overview':
        return (
          <SeriesOverview 
            appData={appData} 
            setAppData={setAppData} 
            onNext={() => setCurrentStage('upload')}
          />
        );
      case 'upload':
        return (
          <FileUpload 
            appData={appData} 
            setAppData={setAppData} 
            onNext={nextStage}
            onPrev={prevStage}
          />
        );
      case 'first-race-review':
        return (
          <FirstRaceReview 
            appData={appData} 
            setAppData={setAppData} 
            onNext={nextStage}
            onPrev={prevStage}
          />
        );
      case 'points':
        return (
          <PointsConfiguration 
            appData={appData} 
            setAppData={setAppData} 
            onNext={nextStage}
            onPrev={prevStage}
          />
        );
      case 'apply':
        return (
          <PointsApplication 
            appData={appData} 
            setAppData={setAppData} 
            onNext={nextStage}
            onPrev={prevStage}
          />
        );
      case 'match':
        return (
          <ParticipantMatching 
            appData={appData} 
            setAppData={setAppData} 
            onNext={nextStage}
            onPrev={prevStage}
          />
        );
      case 'results':
        return (
          <ResultsPreview 
            appData={appData} 
            setAppData={setAppData} 
            onPrev={prevStage}
            onNext={nextStage}
            onRestart={restartForAnotherRace}
            onReturnHome={returnToHome}
          />
        );

      case 'publish':
        return (
          <DataPublishing 
            appData={appData} 
            setAppData={setAppData} 
            onPrev={prevStage}
            onReturnHome={returnToHome}
            onAddAnotherRace={restartForAnotherRace}
          />
        );
      default:
        return null;
    }
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="app">
        <AppHeader showAuth={false} />
        <main className="app-main">
          <Login onLogin={handleLogin} />
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <AppHeader showAuth={true} />

      {currentStage !== 'workflow-selection' && (
        <div className="progress-bar">
          {stages.map((stage, index) => (
            <div 
              key={stage.id}
              className={`progress-step ${index <= currentStageIndex ? 'active' : ''} ${index === currentStageIndex ? 'current' : ''}`}
              onClick={() => {
                // Allow clicking on completed steps to go back
                if (index < currentStageIndex) {
                  setCurrentStage(stage.id as ProcessingStage);
                }
              }}
              style={{ cursor: index < currentStageIndex ? 'pointer' : 'default' }}
            >
              <div className="step-icon">{stage.icon}</div>
              <div className="step-title">{stage.title}</div>
            </div>
          ))}
        </div>
      )}

      <main className="app-main">
        {renderCurrentStage()}
      </main>
    </div>
  );
};

export default App; 